/**
 * Módulo del tutorial (grupo "La app y tu equipo", cierra la saga): El asistente de campo (KAN-232).
 *
 * El chat que conoce tu campo: preguntarle en lenguaje natural, por voz,
 * pedirle que te guíe (usa este mismo sistema de flechas) y la cola
 * offline. Último a propósito: primero el usuario aprende a hacerlo solo,
 * el asistente se descubre solo — pero conviene presentarlo una vez.
 *
 * Anclas: chat-launcher (botón flotante de la vaca 🐄).
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

export const asistenteModule: TutorialModuleDef = {
  id: 'asistente',
  title: 'Preguntale al asistente',
  description: 'Tu consultor de campo a mano: preguntale en criollo, por voz, y hasta te guía con flechas. Funciona sin señal.',
  emoji: '🤖',
  image: 'images/tutorial/asistente.png',
  permission: { module: 'tools', action: 'view' },
  grupo: 'equipo',
  route: '/dashboard',
  steps: [
    {
      id: 'concepto-asistente',
      route: '/tutorial',
      text: 'El último de la fila, y el más charlatán: un asistente que conoce TU campo. Le preguntás en criollo y te responde con tus propios datos.',
      narration:
        'Y para cerrar, el más charlatán de la familia: el asistente. Es como un consultor ganadero que vive adentro de la app y, lo mejor, conoce tu campo: no le preguntás genérico, le preguntás por tus vacas, tus cuadros, tus lluvias. Y responde en criollo, como te gusta.',
    },
    {
      id: 'abrir-chat',
      route: '/dashboard',
      target: '[data-ar-guide="chat-launcher"]',
      label: '💬 El asistente (botón flotante)',
      text: 'Tocá el botón verde de chat que flota abajo a la derecha — en cualquier pantalla está. Se abre el chat del asistente.',
      narration:
        '¿Lo ves al botón verde de chat flotando abajo a la derecha? Esa es la puerta: tocalo desde cualquier pantalla y se abre el chat. Cero ceremonia, como mandarle un mensaje al que sabe.',
    },
    {
      id: 'preguntar',
      route: '/dashboard',
      target: '[data-ar-guide="chat-input"]',
      label: 'Escribí tu pregunta acá',
      advanceOnClick: false,
      text: 'Preguntale como se te cante: "¿cuántas preñadas tengo?", "¿qué cuadro tiene menos pasto?" — entiende criollo, no necesita comandos.',
      narration:
        'Y ahora, preguntale lo que quieras, escrito como se te cante: cuántas preñadas tengo, qué cuadro tiene menos pasto, cuánto llovió este invierno. Entiende castellano de persona, no necesita comandos raros. Le preguntás, y contesta con TUS datos, no con teoría.',
    },
    {
      id: 'por-voz',
      route: '/dashboard',
      target: '[data-ar-guide="chat-mic"]',
      label: '🎤 Micrófono (mantener apretado)',
      advanceOnClick: false,
      text: '¿Con las manos ocupadas? Mantené apretado el micrófono 🎤 y hablá: tu pregunta sale como texto y te responde igual.',
      narration:
        'Y si estás con las manos ocupadas, como siempre en el campo, mantené apretado el micrófono y hablá nomás: tu pregunta se convierte en texto sola, y te responde igual. Para cuando estás en la camioneta o en el medio del corral.',
    },
    {
      id: 'que-te-guie',
      route: '/dashboard',
      text: 'La perla: pedile "mostrame dónde se carga tal cosa" — y te guía con flechas por la pantalla, igual que este tutorial. Es tu ayuda siempre a mano.',
      narration:
        'Ahora, la perla del sistema: si no te acordás dónde queda algo, pedile: mostrame dónde se carga una vacuna. Y el asistente saca flechas y cajitas, igual que este tutorial, y te lleva de la mano hasta el lugar. Esta ayuda que estás viendo ahora, la tiene él para todo, cuando quieras.',
    },
    {
      id: 'offline-despedida',
      route: '/dashboard',
      text: '¿Sin señal en el campo? Igual le escribís: queda en cola y se manda solo cuando vuelve la conexión. Este era el último módulo — ¡gracias por acompañarme!',
      narration:
        'Y un último regalo para los rincones sin señal: igual le escribís, queda en cola, y cuando el celular encuentra señal, manda la pregunta sola y te llega la respuesta. Ni el campo más escondido te queda afuera. Este era el último módulo, criollo: ya sabés usar todo el campo digital. Fue un gusto acompañarte, y acordate: la vaquita flota siempre, lista para ayudarte. ¡Buenas!',
    },
  ],
  completionCheck: async () => false, // se completa haciendo el recorrido, no por datos
};
